"use client"

import { Navbar } from "@/components/navbar"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  returnToHome,
  startOnboardingAutoRedirect,
} from "@/lib/auth/onboardingRedirect.js"
import { Copy, Terminal } from "lucide-react"
import { useRouter } from "next/navigation"
import Image from "next/image"
import Link from "next/link"

type BackendMeResponse = {
  platform_key?: string | null
  openclaw_connected?: boolean
}

type VerifyOpenClawResponse = {
  ok?: boolean
  world_agent_count?: number
  dashboard_agent_count?: number
  dashboard_skill_count?: number
}

type BackendMeErrorResponse = {
  error?: string
}

type OnboardingScreenProps = {
  withNavbar?: boolean
}

export function OnboardingScreen({
  withNavbar = true,
}: OnboardingScreenProps) {
  const AUTO_REDIRECT_MS = 3000
  const [platformKey, setPlatformKey] = useState("")
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [verifying, setVerifying] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [verificationSummary, setVerificationSummary] = useState<{
    agentCount: number
    skillCount: number
  } | null>(null)
  const [autoRedirectRemainingMs, setAutoRedirectRemainingMs] = useState(0)
  const router = useRouter()

  const installCommand = "openclaw plugins install @egoai/platform"
  const configCommands = [
    "openclaw config set channels.platform.enabled true;",
    `openclaw config set channels.platform.platformKey "${platformKey || "YOUR_PLATFORM_KEY"}";`,
  ]
  const setupCommands = [installCommand, ...configCommands]

  useEffect(() => {
    const initializePlatformKey = async () => {
      try {
        const response = await fetch("/api/auth/me", {
          method: "GET",
          cache: "no-store",
        })

        const payload = (await response.json()) as
          | BackendMeResponse
          | BackendMeErrorResponse

        if (!response.ok) {
          throw new Error(
            "error" in payload
              ? (payload.error ?? "Failed to load platform key")
              : "Failed to load platform key"
          )
        }

        setPlatformKey(
          "platform_key" in payload ? (payload.platform_key ?? "") : ""
        )
      } catch (error) {
        setLoadError(
          error instanceof Error ? error.message : "Failed to load platform key"
        )
      }

      setLoading(false)
    }

    void initializePlatformKey()
  }, [])

  useEffect(() => {
    if (!copied) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setCopied(false)
    }, 1800)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [copied])

  useEffect(() => {
    if (!verificationSummary) {
      setAutoRedirectRemainingMs(0)
      return
    }

    return startOnboardingAutoRedirect({
      durationMs: AUTO_REDIRECT_MS,
      performance: window.performance,
      requestAnimationFrame: window.requestAnimationFrame.bind(window),
      cancelAnimationFrame: window.cancelAnimationFrame.bind(window),
      onTick: setAutoRedirectRemainingMs,
      onRedirect: () =>
        returnToHome({
          router,
          location: window.location,
        }),
    })
  }, [AUTO_REDIRECT_MS, router, verificationSummary])

  const handleVerify = async () => {
    setVerifying(true)
    setVerifyError(null)

    try {
      const response = await fetch("/api/openclaw/verify", {
        method: "POST",
        cache: "no-store",
      })

      const payload = (await response.json()) as
        | VerifyOpenClawResponse
        | BackendMeErrorResponse

      if (!response.ok) {
        throw new Error(
          "error" in payload
            ? (payload.error ?? "Failed to verify OpenClaw connection")
            : "Failed to verify OpenClaw connection"
        )
      }

      if (!("ok" in payload) || !payload.ok) {
        throw new Error(
          "OpenClaw verification did not complete successfully."
        )
      }

      setVerificationSummary({
        agentCount: payload.dashboard_agent_count ?? payload.world_agent_count ?? 0,
        skillCount: payload.dashboard_skill_count ?? 0,
      })
    } catch (error) {
      setVerifyError(
        error instanceof Error
          ? error.message
          : "Failed to verify OpenClaw connection"
      )
    } finally {
      setVerifying(false)
    }
  }

  const content = (
    <div className="relative flex min-h-[calc(100vh-40px)] items-center justify-center bg-background px-4 py-8">
      <div className="relative w-full max-w-xl">
        <div className="mb-6 text-center">
          <Link href="/" className="inline-flex flex-col items-center gap-3">
            <Image src="/logo.gif" alt="Ego logo" width={42} height={42} />
            <span className="text-[11px] font-medium tracking-[0.28em] uppercase text-foreground">
              Ego World
            </span>
          </Link>
        </div>

        <Card
          variant="content"
          className="rounded-[24px] border-white/10 bg-black/55 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.42)] backdrop-blur-2xl"
        >
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="mb-2 text-[28px] font-semibold tracking-tight text-foreground">
                Connect OpenClaw
              </h1>
              <p className="mx-auto max-w-[32rem] text-[13px] leading-6 text-muted-foreground">
                Copy all three commands, then paste them into your terminal or
                directly into your OpenClaw chat. Once they finish, come back
                here and verify the connection.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-muted-foreground" />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    Terminal
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {copied ? (
                    <span className="text-[11px] font-medium text-emerald-400">
                      Copied!
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={async () => {
                      await navigator.clipboard.writeText(setupCommands.join("\n"))
                      setCopied(true)
                    }}
                    disabled={loading}
                    className="shrink-0 rounded-xl p-2 text-muted-foreground transition-colors hover:bg-white/8 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {setupCommands.map((cmd) => (
                  <code
                    key={cmd}
                    className="font-mono text-sm text-emerald-300 break-all"
                  >
                    {cmd}
                  </code>
                ))}
              </div>
            </div>

            {loadError && (
              <div className="rounded-2xl border border-destructive/20 bg-destructive/[0.08] p-3.5">
                <p className="text-xs leading-5 text-destructive">{loadError}</p>
              </div>
            )}

            {verifyError && (
              <div className="rounded-2xl border border-destructive/35 bg-destructive/[0.14] p-3.5">
                <div className="space-y-2">
                  <p className="text-sm leading-6 text-red-200">{verifyError}</p>
                  <p className="text-xs leading-5 text-red-100/85">
                    <strong className="font-semibold text-red-50">
                      Try reverifying after a short wait.
                    </strong>{" "}
                    Reconnection can sometimes take up to 60 seconds after a restart.
                  </p>
                </div>
              </div>
            )}

            {verificationSummary && (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.08] p-3.5">
                <p className="text-xs leading-5 text-emerald-300/90">
                  OpenClaw is connected. We loaded{" "}
                  {verificationSummary.agentCount} agent
                  {verificationSummary.agentCount === 1 ? "" : "s"} and{" "}
                  {verificationSummary.skillCount} skill
                  {verificationSummary.skillCount === 1 ? "" : "s"}.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {verificationSummary ? (
                <Button
                  className="relative h-12 w-full overflow-hidden rounded-2xl"
                  onClick={() =>
                    returnToHome({
                      router,
                      location: window.location,
                    })
                  }
                >
                  <span
                    className="absolute inset-y-0 left-0 rounded-2xl bg-white/18 transition-[width] duration-100 ease-linear"
                    style={{
                      width: `${((AUTO_REDIRECT_MS - autoRedirectRemainingMs) / AUTO_REDIRECT_MS) * 100}%`,
                    }}
                  />
                  <span className="relative z-10">
                    Go to dashboard
                    {autoRedirectRemainingMs > 0
                      ? ` (${Math.ceil(autoRedirectRemainingMs / 1000)})`
                      : ""}
                  </span>
                </Button>
              ) : (
                <Button
                  className="h-12 w-full rounded-2xl"
                  disabled={loading || !platformKey}
                  onClick={handleVerify}
                  loading={verifying}
                >
                  {verifying ? "Verifying..." : "Verify connection"}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  )

  if (!withNavbar) {
    return content
  }

  return (
    <>
      <Navbar />
      <div className="min-h-[calc(100vh-5rem)] md:min-h-[calc(100vh-6rem)]">
        {content}
      </div>
    </>
  )
}
